'use client';

import RichText from '@app/_blocks/RichText';
import { useCartState } from '@app/_context';
import BoxIcon from '@app/_icons/BoxIcon/BoxIcon';
import CheckIconLarge from '@app/_icons/CheckIconLarge/CheckIconLarge';
import { Dialog } from '@headlessui/react';
import { XIcon } from '@ngg/icons';
import type { ProductPageInfoBlockFragment } from '@server/gql/graphql';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import type { Dictionary } from 'types';

import type { MarketConfiguration } from '@/config/markets/const';
import { cn } from '@/lib/utils';

export default function ProductPageFreeShippingUspItem({
    className,
    marketConfig,
    dictionary,
    usp,
    currentVariantPrice,
    ...props
}: ComponentProps<'button'> & {
    marketConfig: MarketConfiguration;
    dictionary: Dictionary;
    usp: ProductPageInfoBlockFragment;
    currentVariantPrice: number;
}) {
    const [open, setOpen] = useState(false);
    const { cart } = useCartState();

    const cartTotal = useMemo(() => {
        return cart?.totalGrossAmount ?? 0;
    }, [cart?.totalGrossAmount]);

    const willReachFreeShipping = useMemo(() => {
        return (
            cartTotal + currentVariantPrice > marketConfig.freeShippingThreshold
        );
    }, [marketConfig.freeShippingThreshold, currentVariantPrice, cartTotal]);

    const hasReachedFreeShipping = useMemo(() => {
        return cartTotal >= marketConfig.freeShippingThreshold;
    }, [cartTotal, marketConfig.freeShippingThreshold]);

    return (
        <li className={cn('flex items-start', className)}>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                className="relative z-50">
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="relative mx-auto max-h-full max-w-xl items-start overflow-auto bg-white px-8 py-8 text-left prose-hr:my-6 md:py-12">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="absolute right-0 top-2 p-4 outline-none">
                            <XIcon className="text-base" />
                        </button>
                        <RichText
                            content={usp.content}
                            market={marketConfig.code}
                            locale={marketConfig.locale}
                        />
                    </Dialog.Panel>
                </div>
            </Dialog>
            <button
                className="flex grow items-start gap-4 whitespace-break-spaces p-4 text-left"
                onClick={() => setOpen(true)}
                {...props}>
                <div className="relative h-5 w-5 overflow-hidden">
                    <AnimatePresence mode="wait" initial={false}>
                        {willReachFreeShipping || hasReachedFreeShipping ? (
                            <motion.div
                                key="check"
                                initial={{ y: '-100%' }}
                                animate={{ y: 0 }}
                                exit={{ y: '-100%' }} // Moves up when disappearing
                                transition={{
                                    duration: 0.25,
                                    ease: 'easeOut',
                                }}
                                className="absolute">
                                <CheckIconLarge className="h-5 w-5 flex-shrink-0 text-green-500" />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="box"
                                initial={{ y: '100%' }}
                                animate={{ y: 0 }}
                                exit={{ y: '100%' }} // Moves down when disappearing
                                transition={{
                                    duration: 0.25,
                                    ease: 'easeOut',
                                }}
                                className="absolute">
                                <BoxIcon className="h-5 w-5 flex-shrink-0 text-black" />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <AnimatePresence mode="wait" initial={false}>
                    <UspItemAnimation
                        usp={usp}
                        dictionary={dictionary}
                        hasReachedFreeShipping={hasReachedFreeShipping}
                        willReachFreeShipping={willReachFreeShipping}
                    />
                </AnimatePresence>
            </button>
        </li>
    );
}

function UspItemAnimation({
    usp,
    dictionary,
    willReachFreeShipping,
    hasReachedFreeShipping,
}: {
    usp: ProductPageInfoBlockFragment;
    dictionary: Dictionary;
    willReachFreeShipping: boolean;
    hasReachedFreeShipping: boolean;
}) {
    if (hasReachedFreeShipping) {
        return (
            <motion.div
                key="hasReachedFreeShippingMessage"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: 'linear' }}>
                {dictionary.hasReachedFreeShippingMessage ??
                    'Du har uppnått fri frakt på din order!'}
            </motion.div>
        );
    }

    if (willReachFreeShipping) {
        return (
            <motion.div
                key="freeShipppingMessage"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: 'linear' }}>
                {dictionary.freeShippingMessage ??
                    'Den här produkten ger dig fri frakt på din order'}
            </motion.div>
        );
    }

    return (
        <motion.div
            key="uspTitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: 'linear' }}>
            {usp.title}
        </motion.div>
    );
}
